import React, { useEffect, useState, useRef } from 'react';
import { Copy, Check } from 'lucide-react';
import { LoanData } from '../types/loan';
interface LoanTableRowProps {
  data: LoanData;
  onUpdate: (field: keyof LoanData, value: any) => void;
}
function EditableCell({
  value,
  onSave,
  renderView,
  type = 'text'
}: {
  value: string | number;
  onSave: (val: string) => void;
  renderView: () => React.ReactNode;
  type?: string;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);
  const handleSave = () => {
    setIsEditing(false);
    if (editValue !== String(value)) {
      onSave(editValue);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditValue(String(value));
      setIsEditing(false);
    }
  };
  if (isEditing) {
    return <input ref={inputRef} type={type} value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={handleSave} onKeyDown={handleKeyDown} className="w-full min-w-[60px] px-1 py-0.5 text-[12px] border-2 border-[#0f4496] rounded outline-none bg-white text-slate-800" data-id="element-12" />;
  }
  return <div onClick={() => setIsEditing(true)} className="cursor-text hover:bg-slate-100/50 -mx-1 px-1 py-0.5 rounded transition-colors min-h-[20px] flex items-center" data-id="element-13">
      {renderView()}
    </div>;
}
export function LoanTableRow({
  data,
  onUpdate
}: LoanTableRowProps) {
  const cell = 'px-3 py-3.5 whitespace-nowrap text-[12px]';
  const renderText = (value: string) => {
    if (value === '—' || value === 'Empty') {
      return <span className="text-slate-300" data-id="element-14">—</span>;
    }
    return <span className="text-slate-600 truncate" data-id="element-15">{value}</span>;
  };
  const renderLockStatus = (value: string) => {
    if (value === 'Not Locked') {
      return <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[11px] font-semibold border border-amber-100" data-id="element-16">
          Unlocked
        </span>;
    }
    if (value === '—') return <span className="text-slate-300" data-id="element-17">—</span>;
    return <span className="text-slate-600" data-id="element-18">{value}</span>;
  };
  const renderCdStatus = (value: string) => {
    if (value === '—') return <span className="text-slate-300" data-id="element-19">—</span>;
    const styles: Record<string, string> = {
      Incomplete: 'bg-amber-50 text-amber-700 border-amber-100',
      Sent: 'bg-sky-50 text-sky-700 border-sky-100',
      Signed: 'bg-emerald-50 text-emerald-700 border-emerald-100'
    };
    return <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${styles[value] || ''}`} data-id="element-20">
        {value}
      </span>;
  };
  const renderConditions = (value: number | string) => {
    if (value === '—') return <span className="text-slate-300" data-id="element-21">—</span>;
    if (value === 0 || value === '0') return <span className="text-emerald-600 font-bold" data-id="element-22">0</span>;
    const num = Number(value);
    if (!isNaN(num) && num > 5) return <span className="text-amber-600 font-bold" data-id="element-23">{value}</span>;
    return <span className="text-slate-600" data-id="element-24">{value}</span>;
  };
  const renderSmartStatus = (value: string) => {
    if (value === 'Not Requested') return <span className="text-slate-400 text-[11px]" data-id="element-25">Not Req.</span>;
    if (value === 'N/A' || value === '—') return <span className="text-slate-300" data-id="element-26">—</span>;
    if (value === 'Complete') {
      return <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-600 text-[11px] font-semibold border border-emerald-100" data-id="element-27">
          <Check className="w-3 h-3" data-id="element-28" />
          Done
        </span>;
    }
    return <span className="text-[#0f4496] font-semibold text-[12px]" data-id="element-29">{value}</span>;
  };
  return <tr className="border-b border-slate-100/80 hover:bg-slate-50/40 transition-colors" data-id="element-30">
      {/* ---- READ-ONLY CELLS ---- */}
      <td className={cell} data-id="element-31">
        <span className="text-slate-500 font-mono text-[11px]" data-id="element-32">
          {data.loanNumber === '—' ? '—' : data.loanNumber}
        </span>
      </td>
      <td className={cell} data-id="element-33">
        <div className="flex items-center gap-1 group cursor-pointer" data-id="element-34">
          <span className="text-[#0f4496] font-semibold text-[12px] group-hover:underline truncate max-w-[120px]" data-id="element-35">
            {data.clientName}
          </span>
          <Copy className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" data-id="element-36" />
        </div>
      </td>
      <td className={cell} data-id="element-37">
        <span className="text-slate-500 text-[11px]" data-id="element-38">{data.submittedDate}</span>
      </td>
      <td className={cell} data-id="element-39">{renderText(data.teamLeader)}</td>
      <td className={cell} data-id="element-40">{renderText(data.loa)}</td>
      <td className={cell} data-id="element-41">{renderText(data.processor)}</td>
      <td className={cell} data-id="element-42">
        <span className="text-slate-400 text-[11px]" data-id="element-43">
          {data.latestActivity}
        </span>
      </td>
      <td className={cell} data-id="element-44">{renderText(data.lender)}</td>
      <td className={cell} data-id="element-45">{renderText(data.loanType)}</td>
      <td className={cell} data-id="element-46">
        <span className="text-slate-800 font-semibold tabular-nums" data-id="element-47">
          {data.loanAmount === '—' ? '—' : data.loanAmount}
        </span>
      </td>
      <td className={cell} data-id="element-48">
        <span className="text-slate-500 font-semibold" data-id="element-49">{data.state}</span>
      </td>

      {/* ---- EDITABLE CELLS ---- */}
      <td className={cell} data-id="element-50">
        <EditableCell value={data.lockStatus} onSave={val => onUpdate('lockStatus', val)} renderView={() => renderLockStatus(data.lockStatus)} data-id="element-51" />
      </td>
      <td className={cell} data-id="element-52">
        <EditableCell value={data.cdStatus} onSave={val => onUpdate('cdStatus', val as any)} renderView={() => renderCdStatus(data.cdStatus)} data-id="element-53" />
      </td>
      <td className={cell} data-id="element-54">
        <EditableCell value={String(data.remainingConditions)} onSave={val => onUpdate('remainingConditions', isNaN(Number(val)) ? val : Number(val))} renderView={() => renderConditions(data.remainingConditions)} data-id="element-55" />
      </td>
      <td className={cell} data-id="element-56">
        <EditableCell value={data.appraisal} onSave={val => onUpdate('appraisal', val)} renderView={() => renderSmartStatus(data.appraisal)} data-id="element-57" />
      </td>
      <td className={cell} data-id="element-58">
        <EditableCell value={data.appraisalTransfer} onSave={val => onUpdate('appraisalTransfer', val)} renderView={() => renderSmartStatus(data.appraisalTransfer)} data-id="element-59" />
      </td>
      <td className={cell} data-id="element-60">
        <EditableCell value={data.payoff} onSave={val => onUpdate('payoff', val)} renderView={() => renderSmartStatus(data.payoff)} data-id="element-61" />
      </td>
      <td className={cell} data-id="element-62">
        <EditableCell value={data.hoi} onSave={val => onUpdate('hoi', val)} renderView={() => renderSmartStatus(data.hoi)} data-id="element-63" />
      </td>
      <td className={cell} data-id="element-64">
        <EditableCell value={data.title} onSave={val => onUpdate('title', val)} renderView={() => renderSmartStatus(data.title)} data-id="element-65" />
      </td>
      <td className={cell} data-id="element-66">
        <div className="flex items-center justify-center" data-id="element-67">
          <input type="checkbox" checked={data.pushList} onChange={e => onUpdate('pushList', e.target.checked)} className="w-4 h-4 text-[#0f4496] rounded border-slate-300 focus:ring-[#0f4496] cursor-pointer" data-id="element-68" />
        </div>
      </td>
    </tr>;
}